import { useCallback, useRef, useState } from "react";
import { useSpeechToText } from "./useSpeechToText";
import { canRecordAudio, startRecording, trySelfHostedSTT, type AudioRecording } from "./selfHostedVoice";

/**
 * Composer mic input — self-hosted STT primary, browser SpeechRecognition
 * fallback. Same {supported, listening, start, stop, toggle} shape as
 * useSpeechToText so callers (ChatComposer.tsx) don't need to change beyond
 * the import swap.
 *
 * UX difference from the pure-browser hook: self-hosted STT is
 * record-then-transcribe (no live captions while speaking — the transcript
 * arrives once you tap the mic again to stop), because it's a single POST
 * to /api/stt rather than a streaming recognition session. If the browser
 * can't record audio at all, or the self-hosted call fails for any reason
 * (endpoint down, network error, unsupported language), this transparently
 * falls back to the existing live-transcribing browser hook — never a raw
 * error, matching docs/VOICE-0-rule-change.md's fallback requirement.
 */
export function useVoiceInput(lang = "en-IN") {
  const browser = useSpeechToText(lang);
  const [selfHostedListening, setSelfHostedListening] = useState(false);
  const recordingRef = useRef<AudioRecording | null>(null);
  const onResultRef = useRef<((text: string) => void) | null>(null);

  // Self-hosted is "supported" the moment the browser can record audio at
  // all — actual availability (VOICE_INFERENCE_URL configured) is only
  // known after a real attempt, so we optimistically try it and fall back.
  const supported = browser.supported === null ? null : browser.supported || canRecordAudio();

  const stopSelfHosted = useCallback(async () => {
    const recording = recordingRef.current;
    recordingRef.current = null;
    setSelfHostedListening(false);
    if (!recording) return;

    const blob = await recording.stop();
    const result = await trySelfHostedSTT(blob, "auto");
    if (result && onResultRef.current) {
      onResultRef.current(result.text);
    }
    // On failure (result === null), we've already stopped — the user just
    // sees the mic go idle with no transcript, same as an empty browser
    // recognition result. Silent, not a raw error, per the fallback law.
  }, []);

  const start = useCallback(
    (onResult: (text: string) => void) => {
      onResultRef.current = onResult;
      if (!canRecordAudio()) {
        browser.start(onResult);
        return;
      }
      startRecording()
        .then((recording) => {
          recordingRef.current = recording;
          setSelfHostedListening(true);
        })
        .catch(() => {
          // Mic permission denied or no device — fall back to the browser
          // hook, which has its own (separate) permission prompt.
          browser.start(onResult);
        });
    },
    [browser],
  );

  const stop = useCallback(() => {
    if (recordingRef.current) {
      void stopSelfHosted();
    } else {
      browser.stop();
    }
  }, [browser, stopSelfHosted]);

  const toggle = useCallback(
    (onResult: (text: string) => void) => {
      if (selfHostedListening || browser.listening) stop();
      else start(onResult);
    },
    [selfHostedListening, browser.listening, start, stop],
  );

  return {
    supported,
    listening: selfHostedListening || browser.listening,
    start,
    stop,
    toggle,
  };
}
